<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import {
  BadgeCheck,
  Check,
  CircleAlert,
  Copy,
  Download,
  ExternalLink,
  Music2,
  PackageCheck,
  Radio,
  Rocket,
  Smartphone,
} from "@lucide/vue";
import { strToU8, zipSync } from "fflate";
import TrackArtwork from "@/components/TrackArtwork.vue";
import { api } from "@/lib/api";
import type {
  NeteaseReleaseDraft,
  NeteaseReleaseStatus,
  Track,
} from "@/types";

const ARTIST_NAME = "夜栖电波";
const DISTRIBUTOR_NAME = "网易音乐人自主发行";
const NETEASE_CREATOR_URL = "https://music.163.com/creatorcenter";
const NETEASE_PUBLISH_URL =
  "https://music.163.com/musician/artist/manage/album/nalbum/songupload";
const FEATURED_COVER_URL = "/releases/city-before-dawn-cover.jpg";
const route = useRoute();
const tracks = ref<Track[]>([]);
const selectedId = ref("");
const saving = ref(false);
const packaging = ref(false);
const preparing = ref(false);
const loadingDraft = ref(false);
const message = ref("");
const clipboardMessage = ref("");

const form = reactive({
  title: "",
  artist: "",
  genre: "",
  releaseDate: "",
  isrc: "",
  upc: "",
  explicit: false,
  rightsConfirmed: false,
  aiDisclosure: true,
});

const portal = reactive<Omit<NeteaseReleaseDraft, "trackId" | "updatedAt">>({
  releaseType: "普通专辑",
  albumTitle: "",
  songType: "原创",
  songVersion: "录音室",
  language: "国语",
  performer: true,
  promotionCopy: "",
  status: "draft",
});

const selected = computed(() =>
  tracks.value.find((track) => track.id === selectedId.value),
);
const qualityEligible = computed(() =>
  Boolean(
    selected.value &&
      selected.value.sampleRate >= 44100 &&
      selected.value.bitrate >= 320000,
  ),
);
const coverUrl = computed(() =>
  selected.value?.title === "城市醒来之前" ? FEATURED_COVER_URL : "",
);
const checks = computed(() => [
  { label: "母带文件已持久保存", done: Boolean(selected.value) },
  { label: "发行音质 ≥ 320 Kbps / ≥ 44.1 kHz", done: qualityEligible.value },
  {
    label: "歌名、艺人名与曲风已填写",
    done: Boolean(form.title && form.artist && form.genre),
  },
  { label: "普通专辑与歌曲字段已填写", done: Boolean(portal.albumTitle && portal.language) },
  { label: "发行日期已排期", done: Boolean(form.releaseDate) },
  { label: "已确认素材与歌词权利", done: form.rightsConfirmed },
  { label: "已标注 AI 辅助创作", done: form.aiDisclosure },
]);
const score = computed(() =>
  Math.round(
    (checks.value.filter((item) => item.done).length / checks.value.length) * 100,
  ),
);
const releaseStages = computed(() => {
  const rank: Record<NeteaseReleaseStatus, number> = {
    draft: 0,
    prepared: 1,
    waiting_sms: 2,
    submitted: 3,
  };
  const current = rank[portal.status];
  return [
    { key: "draft", label: "资料准备", detail: "母带与字段", done: score.value === 100 },
    { key: "prepared", label: "进入网易云", detail: "普通专辑上传", done: current >= 1 },
    { key: "waiting_sms", label: "手机验证", detail: "验证码人工输入", done: current >= 2 },
    { key: "submitted", label: "提交审核", detail: "等待平台结果", done: current >= 3 },
  ];
});

function suggestedReleaseDate() {
  const date = new Date();
  date.setDate(date.getDate() + 10);
  while (date.getDay() !== 5) date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
}

function defaultPromotion(track: Track) {
  return `《${track.title}》从${track.genre || "城市夜色"}出发，把夜晚的情绪写进旋律。戴上耳机，在属于你的片刻里听见它。`;
}

async function loadTrack(track?: Track) {
  if (!track) return;
  const trackId = track.id;
  form.title = track.title;
  form.artist =
    track.artist && track.artist !== "我的厂牌" ? track.artist : ARTIST_NAME;
  form.genre = track.genre;
  form.releaseDate = track.releaseDate || suggestedReleaseDate();
  form.isrc = track.isrc || "";
  form.upc = track.upc || "";
  loadingDraft.value = true;
  try {
    const { draft } = await api.releaseDraft(trackId);
    if (selectedId.value !== trackId) return;
    portal.releaseType = draft.releaseType;
    portal.albumTitle = draft.albumTitle;
    portal.songType = draft.songType;
    portal.songVersion = draft.songVersion;
    portal.language = draft.language;
    portal.performer = draft.performer;
    portal.promotionCopy = draft.promotionCopy;
    portal.status = draft.status;
  } catch (cause) {
    portal.releaseType = "普通专辑";
    portal.albumTitle = track.title;
    portal.songType = "原创";
    portal.songVersion = "录音室";
    portal.language = track.lyrics.trim() ? "国语" : "纯音乐";
    portal.performer = Boolean(track.lyrics.trim());
    portal.promotionCopy = defaultPromotion(track);
    portal.status = "draft";
    message.value = cause instanceof Error ? cause.message : "发行任务读取失败";
  } finally {
    if (selectedId.value === trackId) loadingDraft.value = false;
  }
}

watch(selected, (track) => void loadTrack(track));

onMounted(async () => {
  tracks.value = (await api.tracks()).tracks;
  selectedId.value = String(route.query.track || tracks.value[0]?.id || "");
});

async function persistDraft(status: NeteaseReleaseStatus = portal.status) {
  if (!selected.value) return;
  const { draft } = await api.updateReleaseDraft(selected.value.id, {
    releaseType: "普通专辑",
    albumTitle: portal.albumTitle,
    songType: portal.songType,
    songVersion: portal.songVersion,
    language: portal.language,
    performer: portal.performer,
    promotionCopy: portal.promotionCopy,
    status,
  });
  portal.status = draft.status;
}

async function saveRelease(silent = false) {
  if (!selected.value || score.value < 100) return false;
  saving.value = true;
  try {
    const result = await api.updateTrack(selected.value.id, {
      title: form.title,
      artist: form.artist,
      genre: form.genre,
      releaseDate: form.releaseDate,
      isrc: form.isrc,
      upc: form.upc,
      status: "scheduled",
    });
    await persistDraft();
    const index = tracks.value.findIndex((track) => track.id === result.track.id);
    if (index >= 0) tracks.value[index] = result.track;
    if (!silent) {
      message.value = `发行资料已保存到数据库，${DISTRIBUTOR_NAME}发行包已经就绪。`;
    }
    return true;
  } catch (cause) {
    message.value = cause instanceof Error ? cause.message : "保存失败";
    return false;
  } finally {
    saving.value = false;
  }
}

function safeFileName(value: string) {
  return value.replace(/[\\/:*?"<>|]/g, "-").trim() || "wavecraft";
}

function portalText() {
  if (!selected.value) return "";
  return [
    `网易云发行资料｜${form.title}`,
    "",
    `上传作品类型：${portal.releaseType}`,
    `专辑名称：${portal.albumTitle}`,
    `歌曲类型：${portal.songType}`,
    `歌曲版本：${portal.songVersion}`,
    `歌曲名：${form.title}`,
    `歌手名：${form.artist}`,
    `我是演唱者：${portal.performer ? "是" : "否"}`,
    `语种：${portal.language}`,
    `曲风：${form.genre}`,
    `发行日期：${form.releaseDate}`,
    `露骨内容：${form.explicit ? "是" : "否"}`,
    `AI 辅助创作：${form.aiDisclosure ? "是，请按平台要求如实披露" : "否"}`,
    `ISRC：${form.isrc || "由平台分配"}`,
    `UPC / EAN：${form.upc || "由平台分配"}`,
    "",
    "宣传新歌：",
    portal.promotionCopy || "（留空）",
  ].join("\n");
}

async function copyPortalFields(quiet = false) {
  if (!selected.value) return false;
  try {
    await navigator.clipboard.writeText(portalText());
    clipboardMessage.value = "网易云字段已复制，可在发布页直接粘贴核对。";
    if (!quiet) message.value = clipboardMessage.value;
    return true;
  } catch {
    message.value = "浏览器未允许写入剪贴板，请点击页面后重试。";
    return false;
  }
}

async function downloadMaster() {
  if (!selected.value || !qualityEligible.value) {
    message.value = "当前母带未达到网易云音质要求，请重新生成高音质 WAV。";
    return false;
  }
  try {
    const response = await fetch(selected.value.audioUrl);
    if (!response.ok) throw new Error("母带下载失败，请稍后重试。");
    const url = URL.createObjectURL(await response.blob());
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${safeFileName(form.title)}-母带.${selected.value.audioFormat}`;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch (cause) {
    message.value = cause instanceof Error ? cause.message : "母带下载失败";
    return false;
  }
}

async function exportPackage() {
  if (!selected.value || !qualityEligible.value) {
    message.value = "当前母带未达到网易云音质要求，请重新生成高音质 WAV。";
    return;
  }
  packaging.value = true;
  message.value = "";
  try {
    const [audioResponse, coverResponse] = await Promise.all([
      fetch(selected.value.audioUrl),
      fetch(coverUrl.value || FEATURED_COVER_URL),
    ]);
    if (!audioResponse.ok || !coverResponse.ok) {
      throw new Error("母带或封面下载失败，请稍后重试。");
    }

    const folder = safeFileName(`${form.artist}-${form.title}`);
    const metadata = {
      platform: DISTRIBUTOR_NAME,
      releaseType: portal.releaseType,
      albumTitle: portal.albumTitle,
      title: form.title,
      artist: form.artist,
      label: form.artist,
      genre: form.genre,
      songType: portal.songType,
      songVersion: portal.songVersion,
      language: portal.language,
      performer: portal.performer,
      promotionCopy: portal.promotionCopy,
      instrumental: !selected.value.lyrics,
      explicit: form.explicit,
      releaseDate: form.releaseDate,
      territory: "全球",
      copyrightLine: `℗ ${new Date(form.releaseDate).getFullYear()} ${form.artist}`,
      isrc: form.isrc || "由发行平台分配",
      upc: form.upc || "由发行平台分配",
      generatedWith: "MiniMax Music 3.0",
      aiDisclosure: form.aiDisclosure
        ? "本作品为 AI 辅助创作，音乐由 MiniMax Music 3.0 根据原创文字提示生成。"
        : "",
      prompt: selected.value.prompt,
      durationMs: selected.value.durationMs,
      sampleRate: selected.value.sampleRate,
      bitrate: selected.value.bitrate,
      checklist: checks.value,
      exportedAt: new Date().toISOString(),
      note: "发行包已按网易音乐人自主发行整理；平台受理、审核与上架状态以官方后台为准。",
    };
    const disclosure = [
      `《${form.title}》AI 创作说明`,
      "",
      `艺人：${form.artist}`,
      "生成模型：MiniMax Music 3.0",
      `原创提示词：${selected.value.prompt}`,
      "",
      "本作品为 AI 辅助创作。生成过程未使用第三方歌曲、人声、旋律或录音作为参考素材。",
      "提交时如出现 AI 生成、AI 辅助或版权来源字段，应按平台当期规则如实选择。",
    ].join("\n");
    const archive = zipSync(
      {
        [`${folder}/${safeFileName(form.title)}-母带.${selected.value.audioFormat}`]: new Uint8Array(await audioResponse.arrayBuffer()),
        [`${folder}/${safeFileName(form.title)}-封面-3000x3000.jpg`]: new Uint8Array(await coverResponse.arrayBuffer()),
        [`${folder}/网易云字段.txt`]: strToU8(portalText()),
        [`${folder}/发行资料.json`]: strToU8(JSON.stringify(metadata, null, 2)),
        [`${folder}/AI创作说明.txt`]: strToU8(disclosure),
      },
      { level: 0 },
    );
    const blobBytes = archive.buffer.slice(
      archive.byteOffset,
      archive.byteOffset + archive.byteLength,
    ) as ArrayBuffer;
    const url = URL.createObjectURL(
      new Blob([blobBytes], { type: "application/zip" }),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${safeFileName(form.title)}-网易云发行包.zip`;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    message.value = "发行包已下载：母带、封面、网易云字段、元数据和 AI 创作说明均已包含。";
  } catch (cause) {
    message.value = cause instanceof Error ? cause.message : "发行包生成失败";
  } finally {
    packaging.value = false;
  }
}

async function prepareForNetease() {
  if (!selected.value || score.value < 100 || preparing.value) return;
  preparing.value = true;
  message.value = "正在准备网易云发行任务…";
  window.open(NETEASE_PUBLISH_URL, "_blank", "noopener,noreferrer");
  const copyPromise = copyPortalFields(true);
  try {
    const saved = await saveRelease(true);
    if (!saved) return;
    const [copied, downloaded] = await Promise.all([
      copyPromise,
      downloadMaster(),
    ]);
    await persistDraft("prepared");
    message.value = [
      "发行任务已准备并打开网易云：",
      downloaded ? "母带已下载" : "请单独下载母带",
      copied ? "字段已复制" : "请单独复制字段",
      "下一步在网易云选择“普通专辑”并核对资料。",
    ].join(" ");
  } catch (cause) {
    message.value = cause instanceof Error ? cause.message : "发行任务准备失败";
  } finally {
    preparing.value = false;
  }
}

async function updateStage(status: NeteaseReleaseStatus) {
  try {
    await persistDraft(status);
    message.value =
      status === "waiting_sms"
        ? "任务已标记为等待手机验证码；请只在网易云官方页面输入验证码。"
        : "已记录为提交审核。平台通过后，再在作品曲库标记为正式发行。";
  } catch (cause) {
    message.value = cause instanceof Error ? cause.message : "状态保存失败";
  }
}
</script>

<template>
  <div class="release-layout">
    <section class="panel release-form">
      <div class="section-heading border-bottom">
        <div>
          <p class="eyebrow">RELEASE METADATA</p>
          <h3>让作品准备好被平台接收</h3>
        </div>
        <span class="readiness"><b>{{ score }}</b><small>就绪度</small></span>
      </div>

      <div v-if="tracks.length" class="form-section">
        <label class="field">
          <span>选择母带</span>
          <select v-model="selectedId">
            <option v-for="track in tracks" :key="track.id" :value="track.id">
              {{ track.title }} · {{ track.status }}
            </option>
          </select>
        </label>
        <div v-if="selected" class="selected-master">
          <img
            v-if="coverUrl"
            class="selected-cover"
            :src="coverUrl"
            :alt="`${selected.title}发行封面`"
          />
          <TrackArtwork v-else :title="selected.title" />
          <div>
            <strong>{{ selected.title }}</strong>
            <span>{{ (selected.size / 1024 / 1024).toFixed(1) }} MB · {{ selected.sampleRate / 1000 }} kHz · {{ Math.round(selected.bitrate / 1000) }} Kbps · {{ selected.audioFormat.toUpperCase() }}</span>
          </div>
          <audio :src="selected.audioUrl" controls preload="none" />
        </div>
      </div>

      <div v-else class="inline-empty">
        <CircleAlert :size="20" />
        <span><strong>还没有可发行的母带</strong><small>先完成一次音乐生成。</small></span>
        <router-link class="button button-dark button-sm" to="/create">去创作</router-link>
      </div>

      <div class="form-section two-column-form" :class="{ disabled: !selected }">
        <label class="field"><span>发行歌名</span><input v-model="form.title" :disabled="!selected" /></label>
        <label class="field"><span>主要艺人 / 厂牌</span><input v-model="form.artist" :disabled="!selected" /></label>
        <label class="field"><span>主曲风</span><input v-model="form.genre" :disabled="!selected" /></label>
        <label class="field"><span>计划发行日</span><input v-model="form.releaseDate" type="date" :disabled="!selected" /></label>
        <label class="field"><span>ISRC（发行商可分配）</span><input v-model="form.isrc" placeholder="可暂时留空" :disabled="!selected" /></label>
        <label class="field"><span>UPC / EAN（发行商可分配）</span><input v-model="form.upc" placeholder="可暂时留空" :disabled="!selected" /></label>
      </div>

      <div class="portal-fields" :class="{ disabled: !selected || loadingDraft }">
        <div class="portal-fields-heading">
          <div>
            <p class="eyebrow">NETEASE FORM</p>
            <h3>网易云字段预填</h3>
          </div>
          <span><Music2 :size="15" /> 普通专辑</span>
        </div>
        <div class="two-column-form portal-field-grid">
          <label class="field"><span>专辑名称</span><input v-model="portal.albumTitle" :disabled="!selected" /></label>
          <label class="field"><span>上传作品类型</span><select v-model="portal.releaseType" disabled><option>普通专辑</option></select></label>
          <label class="field"><span>歌曲类型</span><select v-model="portal.songType" :disabled="!selected"><option>原创</option><option>翻唱</option><option>remix</option></select></label>
          <label class="field"><span>歌曲版本</span><select v-model="portal.songVersion" :disabled="!selected"><option>录音室</option><option>混音</option><option>伴奏</option><option>现场</option><option>Demo</option></select></label>
          <label class="field"><span>语种</span><select v-model="portal.language" :disabled="!selected"><option>国语</option><option>粤语</option><option>英语</option><option>日语</option><option>韩语</option><option>纯音乐</option><option>其他</option></select></label>
          <label class="portal-performer"><input v-model="portal.performer" type="checkbox" /><span><strong>我是演唱者</strong><small>与网易云页面保持一致</small></span></label>
        </div>
        <label class="field portal-copy-field">
          <span>宣传新歌文案 <small>{{ portal.promotionCopy.length }}/1000</small></span>
          <textarea v-model="portal.promotionCopy" rows="4" maxlength="1000" :disabled="!selected" />
        </label>
      </div>

      <div class="compliance-checks">
        <label><input v-model="form.rightsConfirmed" type="checkbox" /><span><strong>我确认拥有提示词、歌词与上传素材的必要权利</strong><small>不要使用未经授权的人声、旋律或受保护录音。</small></span></label>
        <label><input v-model="form.aiDisclosure" type="checkbox" /><span><strong>在发行资料中保留 AI 辅助创作说明</strong><small>不同平台政策不同，提交前请复核发行商要求。</small></span></label>
        <label><input v-model="form.explicit" type="checkbox" /><span><strong>作品包含露骨内容</strong><small>勾选后请在发行商后台同步标记。</small></span></label>
      </div>

      <div v-if="message" class="success-banner"><PackageCheck :size="18" />{{ message }}</div>
      <div v-if="selected && !qualityEligible" class="quality-warning">
        <CircleAlert :size="18" />
        <span><strong>当前母带不可发行</strong><small>网易云要求不低于 320 Kbps、44.1 kHz。旧版 256 Kbps MP3 必须重新生成，转码不能提升真实音质。</small></span>
        <router-link class="button button-dark button-sm" to="/create">重新生成</router-link>
      </div>

      <div class="release-actions release-actions-primary">
        <button class="button button-lime release-launch" :disabled="!selected || score < 100 || preparing" @click="prepareForNetease">
          <Rocket :size="18" />{{ preparing ? "正在准备…" : "一键准备并打开网易云" }}
        </button>
      </div>
      <div class="release-actions release-actions-secondary">
        <button class="button button-dark" :disabled="!selected || score < 100 || saving" @click="saveRelease()"><BadgeCheck :size="17" />{{ saving ? "保存中…" : "保存发行任务" }}</button>
        <button class="button button-ghost" :disabled="!selected" @click="copyPortalFields()"><Copy :size="17" />复制网易云字段</button>
        <button class="button button-ghost" :disabled="!selected || !qualityEligible" @click="downloadMaster"><Download :size="17" />下载母带</button>
        <button class="button button-ghost" :disabled="!selected || !qualityEligible || packaging" @click="exportPackage"><PackageCheck :size="17" />{{ packaging ? "正在打包…" : "完整发行包" }}</button>
      </div>
    </section>

    <aside class="release-side">
      <article class="panel release-flow-card">
        <div class="section-heading">
          <div><p class="eyebrow">RELEASE FLOW</p><h3>网易云发行任务</h3></div>
          <span class="score-badge">{{ releaseStages.filter((item) => item.done).length }}/4</span>
        </div>
        <div class="release-timeline">
          <div v-for="(stage, index) in releaseStages" :key="stage.key" :class="{ done: stage.done }">
            <span>{{ stage.done ? "✓" : index + 1 }}</span>
            <p><strong>{{ stage.label }}</strong><small>{{ stage.detail }}</small></p>
          </div>
        </div>
        <div class="release-stage-actions">
          <button class="button button-ghost button-sm" :disabled="portal.status !== 'prepared'" @click="updateStage('waiting_sms')"><Smartphone :size="15" />我已到验证码</button>
          <button class="button button-dark button-sm" :disabled="portal.status !== 'waiting_sms'" @click="updateStage('submitted')"><Check :size="15" />我已提交审核</button>
        </div>
        <p class="release-safety-note">验证码只在网易云官方页面输入。声浪工坊不会读取、上传或保存短信验证码。</p>
      </article>

      <article class="panel">
        <div class="section-heading"><div><p class="eyebrow">QUALITY GATE</p><h3>提交前检查</h3></div><span class="score-badge">{{ checks.filter((item) => item.done).length }}/{{ checks.length }}</span></div>
        <div class="check-list">
          <div v-for="item in checks" :key="item.label" :class="{ done: item.done }"><span><Check :size="14" /></span>{{ item.label }}</div>
        </div>
      </article>

      <article class="panel distributor-card">
        <p class="eyebrow">NETEASE DISTRIBUTION</p>
        <h3><Radio :size="18" /> 网易音乐人已配置</h3>
        <p>艺人品牌：夜栖电波。发行任务会保存到数据库，并整理母带、封面、元数据和 AI 创作说明。验证码、授权确认和最终提交仍在网易云官方页面完成。</p>
        <a class="text-link" :href="NETEASE_PUBLISH_URL" target="_blank" rel="noreferrer">直接进入普通专辑上传 <ExternalLink :size="14" /></a>
        <a class="text-link text-link-secondary" :href="NETEASE_CREATOR_URL" target="_blank" rel="noreferrer">打开音乐人管理后台</a>
      </article>
    </aside>
  </div>
</template>
